import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { AppLocation } from '../../../core/models/interface/geo';
import { isPlatformBrowser } from '@angular/common';
declare var google;

@Component({
  selector: 'app-places-autocomplete',
  templateUrl: './places-autocomplete.component.html',
  styleUrls: ['./places-autocomplete.component.scss'],
})
export class PlacesAutocompleteComponent implements OnInit, OnDestroy {
  @ViewChild('addressSearch')
  public searchElementRef: ElementRef;

  @ViewChild('formCtrl')
  public formCtrl: any;

  @Input()
  inputLabel: string;

  @Input()
  autofocus: boolean;

  @Input()
  disableMatStyle: boolean;

  /**
   * Pre-fill the input value with this property.
   */
  @Input()
  startingInputText: string;

  /**
   * Show this value as the placeholder text for the input.
   */
  @Input()
  placeHolderText: any;

  /**
   * Defaults to type `address`.
   */
  @Input()
  types: string[];

  /**
   * After`resultingLocation` emits, the map will zoom to this level.
   * Defaults to `15`.
   */
  @Input()
  resultingZoom: number;

  @Input()
  showFormSelectHelp: boolean;

  @Input()
  compactMode: boolean;

  @Input()
  fromSearchBar: boolean;

  @Input()
  populateFieldOnEnter: boolean = false;

  /**
   * Emit's AppLocation object when the autocomplete `placed_changed` event occurs.
   */
  @Output()
  resultingLocation = new EventEmitter<AppLocation>();

  /**
   * Emits when `ngModelChange` fires.  This is mainly used as a part of form validation, so
   * the parent can set the form to invalid once `changeOccurred` emits, and mark it valid
   * again after `resultingLocation` fires.
   */
  @Output()
  changeOccurred = new EventEmitter<boolean>();

  @Output()
  userSelectedLocation = new EventEmitter<boolean>();

  @Output()
  onInput = new EventEmitter<string>();

  userInput: string;
  location: AppLocation;
  validEmitted = false;
  isBrowser: boolean;
  private placesTimeout: any = null;

  constructor(
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.location = {
      position: {
        lat: 27.964157,
        lng: -82.452606,
      },
      addressComponents: [],
      formattedAddress: '',
      vicinity: '',
      zoom: 8,
    };
  }

  scroll = (event): void => {
    let autocompleteResults = document.getElementsByClassName('pac-container');

    // all these ts-ignores are for the .style property, which seems to be added at runtime
    if (autocompleteResults.length > 0) {
      for (let autocompleteResultsKey in autocompleteResults) {
        if (
          autocompleteResults[autocompleteResultsKey] &&
          // @ts-ignore
          autocompleteResults[autocompleteResultsKey].style &&
          // @ts-ignore
          autocompleteResults[autocompleteResultsKey].style.display !== 'none'
        ) {
          // @ts-ignore
          autocompleteResults[autocompleteResultsKey].style.display = 'none';
        }
      }
    }
  };

  ngOnInit() {
    this.initGooglePlacesInput();
    window.addEventListener('scroll', this.scroll, true);
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this.scroll, true);
  }

  private initGooglePlacesInput(): void {
    this.setCurrentPosition();

    if (!this.types) {
      this.types = [];
    }
  }

  async searchInputChanged(result) {
    if (result && result.geometry) {
      this.location = {
        position: {
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng(),
        },
        vicinity: result.vicinity,
        zoom: 11,
        formattedAddress: result.formatted_address,
        addressComponents: result.address_components,
      };
      this.userSelectedLocation.emit(true);
    }
    this.emitResult(this.location);
  }

  async getPlacesPredictions(input) {
    if (this.populateFieldOnEnter && input.key === 'Enter' && this.location) {
      this.searchElementRef.nativeElement.blur();
      this.startingInputText = this.location.vicinity
        ? this.location.vicinity
        : this.location.formattedAddress;
      this.userSelectedLocation.emit(true);
    } else {
      if (this.placesTimeout) {
        clearTimeout(this.placesTimeout);
      }
      this.placesTimeout = setTimeout(async () => {
        await this.fetchPlacesResults(input);
      }, 100);
    }
  }

  async fetchPlacesResults(input) {
    if (this.isBrowser && this.fromSearchBar && input.target.value) {
      let prediction;
      let autocompleteService = new google.maps.places.AutocompleteService();
      let placesService = new google.maps.places.PlacesService(
        document.createElement('div'),
      );
      await autocompleteService.getPlacePredictions(
        { input: input.target.value, types: this.types },
        (predictions) => {
          prediction = predictions[0];
        },
      );

      if (prediction) {
        const request = {
          placeId: prediction.place_id,
        };
        await placesService.getDetails(request, (result, status) => {
          if (result) {
            this.location = {
              position: {
                lat: result.geometry.location.lat(),
                lng: result.geometry.location.lng(),
              },
              vicinity: result.vicinity,
              zoom: 11,
              formattedAddress: result.formatted_address,
              addressComponents: result.address_components,
            };
            this.emitResult(this.location);
          }
        });
      }
    }
  }

  change(event): void {
    this.validEmitted = false;
    this.changeOccurred.emit(true);
    this.onInput.emit(event);
  }

  /**
   * Asks browser's permission for location.
   */
  private setCurrentPosition() {
    if (
      this.isBrowser &&
      'geolocation' in navigator &&
      !this.startingInputText &&
      !this.fromSearchBar
    ) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.location.markerLatitude = position.coords.latitude;
        this.location.markerLongitude = position.coords.longitude;
        this.location.zoom = 12;
        this.emitResult(this.location);
      });
    }
  }

  private emitResult(location: AppLocation): void {
    this.resultingLocation.emit(location);
    this.validEmitted = true;
  }
}
